import { deleteImages, uploadMatchingImages } from "firebase_/fire_storage";
import { formatDateTimeNowForFirestore } from "util/function/function";
import { newMatchSend } from "api/post/post_matching";
import {
  getMyMatchesApi,
  getRequiredApi,
  getDoneMatchApi,
  getMatchDetailApi,
  getMatchCountApi,
  getRequiredPageApi,
  getDonePageApi,
  createMatchApi,
  updateMatchApi,
  editMatchApi,
  updateMatchImagesApi,
  deleteMatchApi,
  acceptMatchApi,
  postMatchRatingApi,
  normalizeMatchRow,
} from "api/match";
import {
  getRepairCategoryApi,
  getRepairDetailApi,
  getRepairDetail2Api,
} from "api/repair";

// lastCreateDate 가 존재하면 다음 페이지로 단순화. 서버가 오프셋 기반이라 실제 페이지 번호는
// 호출측이 별도로 관리해야 한다. 화면이 amount 단위로 요청하고 lastCreateDate 로 다음 페이지를
// 요청하는 패턴을 그대로 받아들이되, 추후 필요시 호출측을 페이지 기반으로 점진 개편한다.
function pickPage(lastCreateDate) {
  return lastCreateDate ? 2 : 1;
}

function withTitleAndCategory(row) {
  const n = normalizeMatchRow(row);
  const nameList = Array.isArray(n.categoryListName) ? n.categoryListName : [];
  return {
    ...n,
    title: `${nameList[0] || ""} ${nameList[1] || ""} ${nameList[2] || ""}`.trim(),
    category: n.selectList,
  };
}

// ==================== 내 수리 요청 목록 ====================

export const getMatchingList = async (id, amount, lastCreateDate, status) => {
  try {
    const [list, totalCount] = await Promise.all([
      getMyMatchesApi(id, { page: pickPage(lastCreateDate), limit: amount || 20, status }),
      getMatchCountApi({ id, status }),
    ]);
    return [totalCount, list.map(withTitleAndCategory)];
  } catch (error) {
    console.error("failed getMatchingList:", error);
    return [];
  }
};

export const getMatchingListPrev = async (id, amount, lastCreateDate, status) => {
  try {
    const [list, totalCount] = await Promise.all([
      getMyMatchesApi(id, { page: 1, limit: amount || 20, status }),
      getMatchCountApi({ id, status }),
    ]);
    return [totalCount, list.map(withTitleAndCategory).reverse()];
  } catch (error) {
    console.error("failed getMatchingListPrev:", error);
    return [];
  }
};

export const getMatchingListCount = async (id, status) => {
  try {
    // field: "id" 를 넘겨야 백엔드가 '내 요청'으로 필터링한다.
    // (없으면 status 만으로 전체 사용자 매칭을 세어 실제 목록과 숫자가 어긋난다)
    return await getMatchCountApi({ id, status, field: "id" });
  } catch (error) {
    console.error("failed getMatchingListCount:", error);
    return 0;
  }
};

export const getMyMatching = async (id) => {
  try {
    const list = await getMyMatchesApi(id, { page: 1, limit: 100 });
    return list;
  } catch (error) {
    console.error("failed getMyMatching:", error);
    return [];
  }
};

// 매칭 상세
export const getMatchingOne = async (docId) => {
  try {
    const result = await getMatchDetailApi(docId);
    if (Array.isArray(result)) return result[0] || null;
    return result || null;
  } catch (error) {
    console.error("failed getMatchingOne:", error);
    return null;
  }
};

// 전체 공개 요청 목록 (expert 측)
export const getAllMatchingList = async (amount, lastCreateDate, category) => {
  try {
    const [list, totalCount] = await Promise.all([
      getRequiredPageApi({ page: pickPage(lastCreateDate), limit: amount || 20, category }),
      getMatchCountApi({ status: "0" }),
    ]);
    return [totalCount, list];
  } catch (error) {
    console.error("failed getAllMatchingList:", error);
    return [];
  }
};

// expert 가 수락한 목록
export const getMatchedList = async (id) => {
  try {
    return await getDoneMatchApi(id);
  } catch (error) {
    console.error("failed getMatchedList:", error);
    return [];
  }
};

// ==================== 카테고리 ====================

export const getMatchingCategory = async () => {
  try {
    return await getRepairCategoryApi();
  } catch (error) {
    console.error("failed getMatchingCategory:", error);
    return [];
  }
};

export const getMatchingSubCategory = async (tag) => {
  try {
    const list = await getRepairDetailApi(tag);
    if (list.length > 0) list.push({ tag, name: "기타" });
    return list;
  } catch (error) {
    console.error("failed getMatchingSubCategory:", error);
    return [];
  }
};

export const getMatchingSubDetailCategory = async (tag) => {
  try {
    const list = await getRepairDetail2Api(tag);
    if (list.length > 0) list.push({ tag, name: "기타" });
    return list;
  } catch (error) {
    console.error("failed getMatchingSubDetailCategory:", error);
    return [];
  }
};

// ==================== 작성 / 수정 / 삭제 ====================

export const createMatchingRequest = async (
  userData,
  categoryList,
  categoryName,
  etc,
  images,
) => {
  try {
    const body = {
      categoryList: [
        categoryList[0] ?? 0,
        categoryList[1] ?? 0,
        categoryList[2] ?? 0,
      ],
      categoryListName: [
        categoryName[0] ?? "",
        categoryName[1] ?? "",
        categoryName[2] ?? "",
      ],
      selectList: categoryName[0] ?? "",
      etc,
      address: userData.address,
      addressDetail: userData.addressDetail,
      id: userData.id,
      name: userData.name,
      phoneNumber: userData.phone,
      expertId: "",
      expertName: "",
      matchDate: "",
      point: "0",
      rating: false,
      status: "0",
      imageUrls: [],
    };

    const created = await createMatchApi(body);
    if (!created || !created.success) return false;
    const docId = created.docId;

    let imageUrls = [];
    if (images && images.length > 0) {
      imageUrls = await uploadMatchingImages(docId, images);
      await updateMatchImagesApi({ docId, imageUrls });
    }

    const matchMap = {
      ...body,
      imageUrls,
      docId,
      createDate: formatDateTimeNowForFirestore(),
    };
    await newMatchSend(matchMap);

    return true;
  } catch (error) {
    console.error("failed createMatchingRequest:", error);
    return false;
  }
};

export async function editMatch(
  docId,
  categoryList,
  categoryName,
  etc,
  selectedImages = [],
) {
  try {
    const prev = await getMatchingOne(docId);
    const prevImageUrls = (prev && prev.imageUrls) || [];

    const keepUrls = selectedImages.filter((img) => typeof img === "string");
    const newFiles = selectedImages.filter((img) => typeof img !== "string");

    let uploadedUrls = [];
    if (newFiles.length > 0) {
      uploadedUrls = await uploadMatchingImages(docId, newFiles);
    }

    const deletedUrls = prevImageUrls.filter(
      (u) => !keepUrls.includes(u),
    );
    if (deletedUrls.length > 0) await deleteImages(deletedUrls);

    const imageUrls = [...keepUrls, ...uploadedUrls];

    const result = await editMatchApi({
      docId,
      categoryList: [
        categoryList[0] ?? 0,
        categoryList[1] ?? 0,
        categoryList[2] ?? 0,
      ],
      categoryListName: [
        categoryName[0] ?? "",
        categoryName[1] ?? "",
        categoryName[2] ?? "",
      ],
      selectList: categoryName[0] ?? "",
      etc,
      imageUrls,
    });

    return !!(result && result.success);
  } catch (error) {
    console.error("요청 수정 실패:", error);
    return false;
  }
}

export async function editMatchAll(
  docId,
  categoryList,
  categoryName,
  etc,
  selectedImages,
) {
  return editMatch(docId, categoryList, categoryName, etc, selectedImages);
}

export async function deleteMatch(docId) {
  try {
    const result = await deleteMatchApi(docId);
    return !!(result && result.success);
  } catch (error) {
    console.error("요청 삭제 실패:", error);
    return false;
  }
}

// ==================== 별점 / 수락 ====================

export async function giveRating(docId, expertId, userId, rating) {
  try {
    // 서버가 중복 체크 + 별점 insert + match.rating 플래그를 함께 처리한다고 가정.
    const result = await postMatchRatingApi({
      matchDocId: docId,
      expertId,
      id: userId,
      score: rating,
    });
    if (result && result.error === "duplicated") return "duplicated";
    return !!(result && result.success);
  } catch (error) {
    console.error("별점 주기 실패:", error);
    return false;
  }
}

export const acceptMatching = async (docId, expertId, expertName) => {
  try {
    const result = await acceptMatchApi({ docId, expertId, expertName });
    if (!result) return { status: "error", message: "알 수 없는 오류가 발생했습니다." };
    if (result.success) return { status: "done", message: "매칭에 성공하였습니다." };
    // 백엔드 응답 shape: { success:false, status:'point'|'error', message:'...' }
    if (result.status === "point") {
      return { status: "point", message: result.message || "포인트가 부족합니다." };
    }
    if (result.status === "error") {
      return { status: "error", message: result.message || "유효하지 않은 매칭입니다." };
    }
    // (구버전 응답) error 필드만 오는 경우 처리
    if (result.error === "not_enough_point") {
      return { status: "point", message: "포인트가 부족합니다." };
    }
    if (result.error === "not_found" || result.error === "already_accepted") {
      return { status: "error", message: "이미 수락되었거나 유효하지 않은 매칭입니다." };
    }
    return {
      status: "error",
      message: result.message || result.error || "알 수 없는 오류가 발생했습니다.",
    };
  } catch (err) {
    console.error("acceptMatching 실패:", err);
    return { status: "error", message: "알 수 없는 오류가 발생했습니다." };
  }
};
